import React, { useState } from 'react';
import { Phone, MessageCircle, Mail, Navigation, Send, MapPin, Clock, Loader2, CheckCircle, Users } from 'lucide-react';
import { contactApi } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await contactApi.submit(formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', subject: '', message: '' });
      }, 5000);
    } catch {
      setError('Fehler beim Senden. Bitte versuchen Sie es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  const quickActions = [
    {
      icon: Phone,
      label: t('contact.callUs'),
      sub: '+49 511 2773755',
      href: 'tel:+495112773755',
      color: 'bg-green-500 hover:bg-green-600',
      testId: 'contact-call-btn',
    },
    {
      icon: MessageCircle,
      label: t('contact.whatsapp'),
      sub: '+49 176 80022580',
      href: 'https://wa.me/4917680022580',
      color: 'bg-[#25D366] hover:bg-[#1fb855]',
      testId: 'contact-whatsapp-btn',
    },
    {
      icon: Mail,
      label: t('contact.email'),
      sub: 'kontakt@icg-hannover.de',
      href: 'mailto:kontakt@icg-hannover.de',
      color: 'bg-blue-500 hover:bg-blue-600',
      testId: 'contact-email-btn',
    },
    {
      icon: Navigation,
      label: t('contact.route'),
      sub: 'Am Klagesmarkt 29',
      href: 'https://www.google.com/maps/dir/?api=1&destination=Am+Klagesmarkt+29,+30159+Hannover',
      color: 'bg-red-500 hover:bg-red-600',
      testId: 'contact-route-btn',
    },
    {
      icon: Users,
      label: t('contact.whatsappGroup'),
      sub: t('contact.whatsappGroupSub'),
      href: 'https://wa.me/4917680022580?text=Hallo%2C%20ich%20interessiere%20mich%20f%C3%BCr%20die%20ICG%20Hannover',
      color: 'bg-[#25D366] hover:bg-[#1fb855]',
      testId: 'contact-whatsapp-group-btn',
    },
  ];

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {t('contact.title')}
          </h2>
          <div className="w-20 h-1 bg-[#0f2440] mx-auto mb-6"></div>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        {/* Personal Welcome CTA Banner */}
        <div className="max-w-4xl mx-auto mb-12 bg-gradient-to-r from-[#0f2440] to-[#1a3a5c] rounded-2xl p-8 md:p-10 text-center" data-testid="contact-welcome-banner">
          <p className="text-white/90 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            {t('contact.ctaText')}
          </p>
        </div>

        {/* Quick Action Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-5xl mx-auto mb-16">
          {quickActions.map((action) => (
            <a
              key={action.testId}
              href={action.href}
              target={action.href.startsWith('http') ? '_blank' : undefined}
              rel={action.href.startsWith('http') ? 'noopener noreferrer' : undefined}
              data-testid={action.testId}
              className={`${action.color} text-white rounded-2xl p-5 flex flex-col items-center gap-3 
                         transition-all hover:scale-105 hover:shadow-xl text-center`}
            >
              <action.icon className="w-8 h-8" />
              <span className="font-bold text-base">{action.label}</span>
              <span className="text-white/80 text-xs">{action.sub}</span>
            </a>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="bg-gray-50 rounded-2xl p-8" data-testid="contact-form-section">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.formTitle')}</h3>

            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center" data-testid="contact-success">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-green-800 font-semibold text-lg">Nachricht gesendet!</p>
                <p className="text-green-600 text-sm mt-1">Wir melden uns bald bei dir.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.name')}</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="contact-name"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.emailField')}</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="contact-email"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.subject')}</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    data-testid="contact-subject"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.message')}</label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    data-testid="contact-message"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white"
                  />
                </div>
                {error && <p className="text-red-500 text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="w-full px-6 py-3.5 bg-[#0f2440] text-white rounded-lg font-semibold hover:bg-[#1a3a5c] transition-all 
                             flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />{t('contact.sending')}</>
                  ) : (
                    <><Send className="w-5 h-5" />{t('contact.send')}</>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Info + Map */}
          <div className="space-y-6">
            {/* Info cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-5">
                <Clock className="w-6 h-6 text-[#0f2440] mb-2" />
                <h4 className="font-bold text-gray-900 text-sm mb-2">{t('contact.hours')}</h4>
                <p className="text-xs text-gray-600">{t('contact.sundayService')}</p>
                <p className="text-xs text-gray-600 mt-1">{t('contact.thursdayPrayer')}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-5">
                <Phone className="w-6 h-6 text-[#0f2440] mb-2" />
                <h4 className="font-bold text-gray-900 text-sm mb-2">{t('contact.phone')}</h4>
                <p className="text-xs text-gray-600">+49 511 2773755</p>
                <p className="text-xs text-gray-600 mt-1">+49 176 80022580</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <MapPin className="w-6 h-6 text-[#0f2440] mb-2" />
              <h4 className="font-bold text-gray-900 text-sm mb-2">{t('contact.address')}</h4>
              <p className="text-sm text-gray-600">Am Klagesmarkt 29, 30159 Hannover</p>
            </div>

            {/* OpenStreetMap */}
            <div className="rounded-xl overflow-hidden shadow-lg h-[280px]" data-testid="contact-map">
              <iframe
                title="ICG Hannover Standort"
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                src="https://www.openstreetmap.org/export/embed.html?bbox=9.7340%2C52.3780%2C9.7400%2C52.3810&layer=mapnik&marker=52.3795%2C9.7370"
                style={{ border: 0 }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
